import React, { FC, useEffect, useState } from 'react';
import { PageTitle } from '../../layout/core';
import { adminBreadCrumbs } from '../../routing/adminRoutes';
import { useTranslation } from 'react-i18next';
import { IProviderFiltersProps, ProviderFilters } from '../../components/provider/ProviderFilters';
import {
  fetchGatewayRegisterList,
  fetchProviderData, setProviders,
  setQuarantineRelease,
} from '../../store/payments/actions/providers';
import { usePaymentContext } from '../../store/payments/payments-context';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../../components/Navbar';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import {
  getProvidersTabs, IReleaseState,
  PROVIDERS_TABS,
} from '../../routing/routing-helper/provider-navigation';
import { fetchWhiteListedPartnersList } from '../../store/merchnts/actions/partners';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { IModalWindow, MODAL_TYPE, ModalWindow } from '../../components/modal/ModalWindow';
import { ProviderDetails } from '../../components/provider/ProviderDetails';
import { ProviderActiveModal } from '../../components/provider/ProviderActiveModal';
import { IProviderActiveParams } from '../../components/provider/ProviderDetailsHeader';
import { getInitialProvidersNavTab, PROVIDERS_BASE_URL } from '../../routing/providersRoutes';
import { paymentsActionTypes } from '../../store/payments/payments-action-types';

interface Props {
  children: React.ReactElement;
}

export const ProviderPage: FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const { paymentContextState: { providerData }, dispatch } = usePaymentContext();
  const { dispatch: merchantDispatch } = useMerchants();

  const [activePage, setActivePage] = useState<PROVIDERS_TABS>(getInitialProvidersNavTab(pathname));

  const [isActive, setIsActive] = useState<string>('1');
  const [partnerId, setPartnerId] = useState<string>('');

  const [error, setError] = useState<string[]>(null);

  const [isOpenProviderDetails, setIsOpenProviderDetails] = useState<boolean>(false);
  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });
  const [releaseState, setReleaseState] = useState<IReleaseState>({
    isOpen: false,
    providerId: '',
  });
  const [deactivateModalState, setDeactivateModalState] = useState<IReleaseState>({
    isOpen: false,
    providerId: '',
  });

  const filtersFromUrl: IProviderFiltersProps = parseSearchParams(search) || {};

  const quarantinedProvidersList = providerData?.filter(provider => provider?.cycleMetrics?.quarantine_AddedOn);

  const handleModalClose = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

  const closeProviderDetailsModal = async (): Promise<void> => {
    setIsOpenProviderDetails(false);

    const params = { ...filtersFromUrl };
    delete params.providerId;

    navigate(`${pathname}?${makeSearchParams(params)}`);
  };

  const closeReleaseModal = (): void => setReleaseState({ isOpen: false, providerId: '' });
  const closeDeactivateModal = (): void => setDeactivateModalState({ isOpen: false, providerId: '' });

  useEffect(() => {
    void fetchGatewayRegisterList(dispatch);
  }, []);

  useEffect(() => {
    void fetchWhiteListedPartnersList(merchantDispatch);
  }, []);

  useEffect(() => {
    if (providerData.length && filtersFromUrl?.providerId) {
      setIsOpenProviderDetails(true);
    }
  }, [search, providerData]);

  const releasedSuccessModal = async (): Promise<void> => {
    closeReleaseModal();
    const handleClose = async (): Promise<void> => {
      handleModalClose();
      await fetchProviderData({ dispatch, searchParams: filtersFromUrl });
    };

    setModalState({
      isOpen: true,
      modalType: MODAL_TYPE.APPROVE,
      modalTitle: 'Release from quarantine',
      modalText: 'Provider has been successfully released from quarantine.',
      confirmButtonText: 'Ok',
      minHeight: '200px',
      onConfirm: handleClose,
      onClose: handleClose,
    });
  };

  const deactivateSuccessModal = async (): Promise<void> => {
    closeDeactivateModal();
    const handleClose = async (): Promise<void> => {
      handleModalClose();
      await fetchProviderData({ dispatch, searchParams: filtersFromUrl });
    };

    setModalState({
      isOpen: true,
      modalType: MODAL_TYPE.APPROVE,
      modalTitle: 'Deactivating provider',
      modalText: 'Provider has been successfully deactivated.',
      confirmButtonText: 'Ok',
      minHeight: '200px',
      onConfirm: handleClose,
      onClose: handleClose,
    });
  };

  const handleDeactivateProvider = async (): Promise<void> => {
    try {
      const params: IProviderActiveParams = {
        isActive: false,
        id: deactivateModalState?.providerId
      };

      await setProviders({ dispatch, params, setError });
      await deactivateSuccessModal();
    } catch (e) {
      closeDeactivateModal();
    }
  };

  const handleTabChange = (tab: PROVIDERS_TABS): void => {
    setActivePage(tab);

    navigate(`${PROVIDERS_BASE_URL}/${tab}?${makeSearchParams(filtersFromUrl)}`);

    dispatch({ type: paymentsActionTypes.SET_PROVIDER_DATA_LOADING, payload: true });
  };

  const listener = async (search: IProviderFiltersProps): Promise<void> => {
    let searchParams: IProviderFiltersProps;

    if (Object.keys(search)?.length) {
      searchParams = { ...search };

      setPartnerId(search?.partnerId ?? '');

      switch (searchParams?.isActive) {
        case 'true':
          setIsActive('1');
          break;
        case 'false':
          setIsActive('2');
          break;
        default:
          setIsActive('');
          break;
      }


    } else {
      searchParams = {
        isActive: 'true',
        partnerId: '',
      };
    }

    await fetchProviderData({ dispatch, searchParams });
  };

  return (
    <div>
      <PageTitle breadcrumbs={adminBreadCrumbs}>{t('MENU.PROVIDER')}</PageTitle>

      <ProviderFilters
        isActive={isActive}
        setIsActive={setIsActive}
        partnerId={partnerId}
        setPartnerId={setPartnerId}
      />

      <Navbar
        tabsList={getProvidersTabs(quarantinedProvidersList?.length)}
        active={activePage}
        onSelect={handleTabChange}
      />

      {React.cloneElement(children, { listener, quarantinedProvidersList, setReleaseState, setDeactivateModalState })}

      <ModalWindow
        {...modalState}
      />

      <ModalWindow
        isOpen={isOpenProviderDetails}
        isRightSideModal={true}
        size='lg'
        modalTitle={t('MENU.PROVIDER')}
        content={<ProviderDetails setReleaseState={setReleaseState} />}
        contentClassName='d-block'
        cypressAttribute='provider-details'
        onClose={closeProviderDetailsModal}
      />

      <ModalWindow
        isOpen={releaseState.isOpen}
        modalTitle='Release from quarantine'
        modalType={MODAL_TYPE.DECLINE}
        onCancel={closeReleaseModal}
        onClose={closeReleaseModal}
        confirmButtonText='Release'
        additionalConfirmButtonClass='w-100px'
        onConfirm={async (): Promise<void> => {
          await setQuarantineRelease({
            providerId: releaseState.providerId,
            onSuccess: releasedSuccessModal,
            onError: closeReleaseModal,
          });
        }}
        content={
          <div className="px-8">
            <div className="mb-5">Are you sure you want to release this provider from quarantine?</div>
            <div className="mb-5">
              Please note that if the issue with the MID is not fully resolved, the MID will automatically be
              quarantined again until the issue is addressed.
            </div>
            <div>
              Proceeding will temporarily lift the quarantine, but it`s important to ensure that all underlying issues
              are fixed to prevent it from being quarantined again.
            </div>
          </div>
        }
      />

      <ModalWindow
        isOpen={deactivateModalState.isOpen}
        modalTitle='Deactivating provider'
        content={<ProviderActiveModal error={error} />}
        contentClassName='d-block'
        backdrop='static'
        minHeight={150}
        onClose={(): void => {
          closeDeactivateModal();
          setError(null);
        }}
        onCancel={(): void => {
          closeDeactivateModal();
          setError(null);
        }}
        onConfirm={handleDeactivateProvider}
        isConfirmDisabled={error?.length > 0}
        additionalConfirmButtonClass='w-120px'
        confirmButtonText='Deactivate'
      />

    </div>
  );
};